import type { Citation } from "@shared/types";

/**
 * Source-visibility policy: only sources the user can actually open are shown.
 * Internal KB material (local files, Feishu wiki/drive, manual drafts, demo
 * cases, video transcripts) has no public, directly-navigable URL, so it is
 * hidden from the "用于作答的来源" list — showing unclickable internal doc names
 * leaks internal structure and confuses end users. Public web results (an
 * http(s) URL) are shown and clickable.
 */
export function isPublicCitation(c: Citation): boolean {
  // The caller's OWN materials are never "public web sources" even though a
  // connected Feishu doc carries an https URL — they surface via the answer's
  // provenance line (你的资料 / 你的飞书 / 小环生成), not the public web-chip row.
  if (c.origin === "personal" || c.origin === "feishu-user" || c.origin === "artifact") {
    return false;
  }
  return typeof c.url === "string" && /^https?:\/\//i.test(c.url);
}

/**
 * Strip a reasoning model's <think> markup from the displayed answer. The harness
 * usually parses thinking into a separate channel, but on some qwen reasoning
 * turns a stray tag (e.g. a leaked closing `</think>`) leaks into the text. Runs
 * on every render so a mid-stream leak is hidden too: removes paired blocks, an
 * unclosed trailing block, a leaked leading close, and any remaining stray tag.
 */
export function stripThinkTags(text: string): string {
  return text
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/<think>[\s\S]*$/i, "")
    .replace(/^\s*<\/think>\s*/i, "")
    .replace(/<\/?think>/gi, "")
    .replace(/^\s+/, "");
}

/**
 * Strip inline 【来源N】 markers that point at hidden (non-public) sources, so
 * the answer text never references a source chip the user cannot see. Markers
 * citing a visible public source are kept; mixed markers keep only the visible
 * numbers. Handles single (【来源2】) and grouped (【来源1、3】) forms.
 */
export function stripHiddenCiteMarkers(text: string, citations: Citation[]): string {
  const visible = new Set(citations.filter(isPublicCitation).map((c) => c.num));
  return text
    .replace(/【\s*来源\s*([\d\s、，,和及]+?)\s*】/g, (_full, nums: string) => {
      const kept = nums
        .split(/[、，,和及\s]+/)
        .map((n) => Number(n))
        .filter((n) => Number.isFinite(n) && visible.has(n));
      return kept.length > 0 ? `【来源${kept.join("、")}】` : "";
    })
    // Tidy punctuation left dangling where a marker was removed mid-sentence.
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\(\s*\)/g, "");
}
